import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { apiGet, apiPost, apiDelete } from '@/utils/api';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  DollarSign,
  Plus,
  Search,
  CheckCircle,
  Clock,
  AlertTriangle,
  FileText,
  CreditCard,
  Trash2,
  ChevronRight,
  Building2,
  TrendingUp,
  Wallet,
  RefreshCw,
  Globe,
  Download,
  Printer
} from 'lucide-react';

// Simplified animation variants for better performance
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } }
};

const itemVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.15 } }
};

// Summary Card Component
const SummaryCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
  <motion.div variants={itemVariants}>
    <Card className={`bg-gradient-to-br ${color} backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">{title}</p>
            <p className="text-white text-2xl font-bold">{value}</p>
            {subtitle && <p className="text-slate-500 text-xs mt-1">{subtitle}</p>}
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${trend === 'up' ? 'bg-emerald-500/20' : trend === 'down' ? 'bg-red-500/20' : 'bg-white/10'}`}>
            <Icon className={`h-6 w-6 ${trend === 'up' ? 'text-emerald-400' : trend === 'down' ? 'text-red-400' : 'text-white/70'}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

// Supplier Card Component
const SupplierPaymentCard = ({ supplier, onViewDetails, onAddPayment, onExportReport }) => {
  const isFullySettled = supplier.outstanding <= 0;
  
  return (
    <motion.div variants={itemVariants}>
      <Card className={`bg-slate-900/60 backdrop-blur-xl border ${isFullySettled ? 'border-emerald-500/30' : 'border-orange-500/30'} rounded-2xl overflow-hidden hover:border-white/20 transition-all group`}>
        <CardContent className="p-5">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isFullySettled ? 'bg-emerald-500/20' : 'bg-orange-500/20'}`}>
                <Building2 className={`h-6 w-6 ${isFullySettled ? 'text-emerald-400' : 'text-orange-400'}`} />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">{supplier.supplierName}</h3>
                <p className="text-slate-500 text-xs flex items-center gap-1">
                  <Globe className="h-3 w-3" />
                  {supplier.supplierCountry || 'Unknown'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => onExportReport(supplier)}
                size="sm"
                variant="outline"
                className="bg-slate-800/50 border-slate-700 hover:bg-slate-700 text-white rounded-lg h-8 w-8 p-0"
                title="Export Report"
              >
                <Download className="h-4 w-4" />
              </Button>
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${isFullySettled ? 'bg-emerald-500/20 text-emerald-400' : 'bg-orange-500/20 text-orange-400'}`}>
                {isFullySettled ? 'SETTLED' : 'OUTSTANDING'}
              </span>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-slate-800/50 rounded-xl p-3 text-center">
              <p className="text-slate-500 text-[10px] uppercase">Total Paid</p>
              <p className="text-emerald-400 font-bold text-lg">AED {supplier.totalPaid.toLocaleString()}</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-3 text-center">
              <p className="text-slate-500 text-[10px] uppercase">Settled</p>
              <p className="text-blue-400 font-bold text-lg">AED {supplier.totalSettled.toLocaleString()}</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-3 text-center">
              <p className="text-slate-500 text-[10px] uppercase">Outstanding</p>
              <p className={`font-bold text-lg ${supplier.outstanding > 0 ? 'text-orange-400' : 'text-emerald-400'}`}>
                AED {supplier.outstanding.toLocaleString()}
              </p>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex gap-2">
            <Button
              onClick={() => onViewDetails(supplier)}
              variant="outline"
              className="flex-1 bg-slate-800/50 border-slate-700 hover:bg-slate-700 text-white rounded-xl"
            >
              View Details
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
            <Button
              onClick={() => onAddPayment(supplier)}
              className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-xl"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Payment
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Payment History Item
const PaymentItem = ({ payment, onSettle, onDelete }) => {
  const isSettled = payment.isSettled;
  const outstanding = payment.amount - (payment.settledAmount || 0);
  
  return (
    <motion.div
      variants={itemVariants}
      className={`bg-slate-800/50 rounded-xl p-4 border ${isSettled ? 'border-emerald-500/30' : 'border-slate-700'}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSettled ? 'bg-emerald-500/20' : 'bg-blue-500/20'}`}>
            {isSettled ? <CheckCircle className="h-5 w-5 text-emerald-400" /> : <Clock className="h-5 w-5 text-blue-400" />}
          </div>
          <div>
            <p className="text-white font-semibold">AED {payment.amount.toLocaleString()}</p>
            <p className="text-slate-500 text-xs">{payment.paymentDate}</p>
          </div>
        </div>
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${isSettled ? 'bg-emerald-500/20 text-emerald-400' : 'bg-orange-500/20 text-orange-400'}`}>
          {isSettled ? 'SETTLED' : `DUE: AED ${outstanding.toLocaleString()}`}
        </span>
      </div>
      
      <div className="space-y-2 mb-3">
        <div className="flex items-start gap-2">
          <FileText className="h-4 w-4 text-slate-500 mt-0.5" />
          <div>
            <p className="text-slate-400 text-xs">Description</p>
            <p className="text-white text-sm">{payment.description}</p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-slate-500 mt-0.5" />
          <div>
            <p className="text-slate-400 text-xs">Reason</p>
            <p className="text-white text-sm">{payment.reason}</p>
          </div>
        </div>
        {payment.referenceNumber && (
          <div className="flex items-start gap-2">
            <CreditCard className="h-4 w-4 text-slate-500 mt-0.5" />
            <div>
              <p className="text-slate-400 text-xs">Reference</p>
              <p className="text-white text-sm">{payment.referenceNumber}</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Progress bar */}
      {!isSettled && (
        <div className="mb-3">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-slate-500">Settlement Progress</span>
            <span className="text-blue-400">{Math.round((payment.settledAmount || 0) / payment.amount * 100)}%</span>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all"
              style={{ width: `${(payment.settledAmount || 0) / payment.amount * 100}%` }}
            />
          </div>
        </div>
      )}
      
      {/* Actions */}
      <div className="flex gap-2">
        {!isSettled && (
          <Button
            onClick={() => onSettle(payment)}
            size="sm"
            className="flex-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30 rounded-lg"
          >
            <CheckCircle className="h-4 w-4 mr-1" />
            Settle
          </Button>
        )}
        <Button
          onClick={() => onDelete(payment)}
          size="sm"
          variant="outline"
          className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border-red-500/30 rounded-lg"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </motion.div>
  );
};

// Add Payment Modal
const AddPaymentModal = ({ isOpen, onClose, supplier, onSubmit }) => {
  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    reason: '',
    paymentDate: new Date().toLocaleDateString('en-GB'),
    paymentMethod: '',
    referenceNumber: ''
  });
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || !formData.description || !formData.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      await onSubmit({
        ...formData,
        amount: parseFloat(formData.amount),
        supplierId: supplier.supplierId
      });
      onClose();
      setFormData({
        amount: '',
        description: '',
        reason: '',
        paymentDate: new Date().toLocaleDateString('en-GB'),
        paymentMethod: '',
        referenceNumber: ''
      });
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border border-white/10 rounded-2xl max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white text-xl font-bold flex items-center gap-2">
            <Plus className="h-5 w-5 text-blue-400" />
            Add Advance Payment
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Add a new advance payment for {supplier?.supplierName}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <label className="text-slate-400 text-sm block mb-1">Amount (AED) *</label>
            <Input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({...formData, amount: e.target.value})}
              placeholder="Enter amount"
              className="bg-slate-800 border-slate-700 text-white rounded-xl"
              required
            />
          </div>
          
          <div>
            <label className="text-slate-400 text-sm block mb-1">Description *</label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="e.g., Advance for worker recruitment"
              className="bg-slate-800 border-slate-700 text-white rounded-xl"
              required
            />
          </div>
          
          <div>
            <label className="text-slate-400 text-sm block mb-1">Reason *</label>
            <Input
              value={formData.reason}
              onChange={(e) => setFormData({...formData, reason: e.target.value})}
              placeholder="e.g., Worker visa processing fees"
              className="bg-slate-800 border-slate-700 text-white rounded-xl"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-slate-400 text-sm block mb-1">Payment Date</label>
              <Input
                value={formData.paymentDate}
                onChange={(e) => setFormData({...formData, paymentDate: e.target.value})}
                placeholder="DD/MM/YYYY"
                className="bg-slate-800 border-slate-700 text-white rounded-xl"
              />
            </div>
            <div>
              <label className="text-slate-400 text-sm block mb-1">Payment Method</label>
              <Input
                value={formData.paymentMethod}
                onChange={(e) => setFormData({...formData, paymentMethod: e.target.value})}
                placeholder="e.g., Bank Transfer"
                className="bg-slate-800 border-slate-700 text-white rounded-xl"
              />
            </div>
          </div>
          
          <div>
            <label className="text-slate-400 text-sm block mb-1">Reference Number</label>
            <Input
              value={formData.referenceNumber}
              onChange={(e) => setFormData({...formData, referenceNumber: e.target.value})}
              placeholder="Bank reference or receipt number"
              className="bg-slate-800 border-slate-700 text-white rounded-xl"
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1 bg-slate-800 border-slate-700 text-white rounded-xl"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-xl"
            >
              {loading ? 'Adding...' : 'Add Payment'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Settle Payment Modal
const SettlePaymentModal = ({ isOpen, onClose, payment, onSubmit }) => {
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  
  const outstanding = payment ? payment.amount - (payment.settledAmount || 0) : 0;
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (parseFloat(amount) > outstanding) {
      toast.error(`Amount cannot exceed outstanding balance of AED ${outstanding.toLocaleString()}`);
      return;
    }
    
    setLoading(true);
    try {
      await onSubmit(payment.id, {
        settledAmount: parseFloat(amount),
        settlementNotes: notes
      });
      onClose();
      setAmount('');
      setNotes('');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border border-white/10 rounded-2xl max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white text-xl font-bold flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-emerald-400" />
            Settle Payment
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Record a settlement for this advance payment
          </DialogDescription>
        </DialogHeader>
        
        <div className="bg-slate-800/50 rounded-xl p-4 my-4">
          <div className="flex justify-between mb-2">
            <span className="text-slate-400">Total Amount:</span>
            <span className="text-white font-bold">AED {payment?.amount?.toLocaleString()}</span>
          </div>
          <div className="flex justify-between mb-2">
            <span className="text-slate-400">Already Settled:</span>
            <span className="text-blue-400 font-bold">AED {(payment?.settledAmount || 0).toLocaleString()}</span>
          </div>
          <div className="flex justify-between pt-2 border-t border-slate-700">
            <span className="text-slate-400">Outstanding:</span>
            <span className="text-orange-400 font-bold">AED {outstanding.toLocaleString()}</span>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-slate-400 text-sm block mb-1">Settlement Amount (AED) *</label>
            <Input
              type="number"
              step="0.01"
              max={outstanding}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`Max: ${outstanding.toLocaleString()}`}
              className="bg-slate-800 border-slate-700 text-white rounded-xl"
              required
            />
            <button
              type="button"
              onClick={() => setAmount(outstanding.toString())}
              className="text-blue-400 text-xs mt-1 hover:underline"
            >
              Settle full amount
            </button>
          </div>
          
          <div>
            <label className="text-slate-400 text-sm block mb-1">Notes</label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Settlement notes..."
              className="bg-slate-800 border-slate-700 text-white rounded-xl"
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1 bg-slate-800 border-slate-700 text-white rounded-xl"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white rounded-xl"
            >
              {loading ? 'Processing...' : 'Confirm Settlement'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Supplier Details Modal
const SupplierDetailsModal = ({ isOpen, onClose, supplier, payments, onSettle, onDelete, onExportReport }) => {
  if (!supplier) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border border-white/10 rounded-2xl max-w-2xl max-h-[85vh] overflow-hidden p-0">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600/20 via-cyan-600/20 to-teal-600/20 p-6 border-b border-white/10">
          <DialogTitle className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-blue-500/30 border border-blue-500/50 flex items-center justify-center">
              <Building2 className="h-7 w-7 text-blue-400" />
            </div>
            <div className="flex-1">
              <span className="text-white font-bold text-2xl">{supplier.supplierName}</span>
              <span className="text-slate-400 text-sm block">Payment History</span>
            </div>
            <Button
              onClick={() => onExportReport && onExportReport(supplier)}
              className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white rounded-xl"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Report
            </Button>
          </DialogTitle>
        </div>
        
        {/* Summary */}
        <div className="p-4 border-b border-white/10">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-emerald-500/10 rounded-xl p-3 text-center border border-emerald-500/30">
              <p className="text-emerald-400 text-xl font-bold">AED {supplier.totalPaid?.toLocaleString()}</p>
              <p className="text-slate-500 text-xs">Total Paid</p>
            </div>
            <div className="bg-blue-500/10 rounded-xl p-3 text-center border border-blue-500/30">
              <p className="text-blue-400 text-xl font-bold">AED {supplier.totalSettled?.toLocaleString()}</p>
              <p className="text-slate-500 text-xs">Settled</p>
            </div>
            <div className={`rounded-xl p-3 text-center border ${supplier.outstanding > 0 ? 'bg-orange-500/10 border-orange-500/30' : 'bg-emerald-500/10 border-emerald-500/30'}`}>
              <p className={`text-xl font-bold ${supplier.outstanding > 0 ? 'text-orange-400' : 'text-emerald-400'}`}>
                AED {supplier.outstanding?.toLocaleString()}
              </p>
              <p className="text-slate-500 text-xs">Outstanding</p>
            </div>
          </div>
        </div>
        
        {/* Payment List */}
        <div className="overflow-y-auto max-h-[50vh] p-4 space-y-3">
          {payments.length === 0 ? (
            <div className="text-center py-12">
              <Wallet className="h-16 w-16 mx-auto mb-4 text-slate-600" />
              <p className="text-slate-500">No payments recorded yet</p>
            </div>
          ) : (
            payments.map(payment => (
              <PaymentItem
                key={payment.id}
                payment={payment}
                onSettle={onSettle}
                onDelete={onDelete}
              />
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Page Component
const AdvancePaymentsPage = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [addPaymentModal, setAddPaymentModal] = useState({ isOpen: false, supplier: null });
  const [settleModal, setSettleModal] = useState({ isOpen: false, payment: null });
  const [detailsModal, setDetailsModal] = useState({ isOpen: false, supplier: null, payments: [] });
  
  // Fetch summary data
  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiGet('/api/advance-payments/summary');
      setSummary(data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
      toast.error('Failed to load advance payments data');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);
  
  // Handle view supplier details
  const handleViewDetails = async (supplier) => {
    try {
      const data = await apiGet(`/api/advance-payments/supplier/${supplier.supplierId}`);
      setDetailsModal({
        isOpen: true,
        supplier: supplier,
        payments: data.payments || []
      });
    } catch (error) {
      toast.error('Failed to load payment details');
    }
  };
  
  // Handle add payment
  const handleAddPayment = async (paymentData) => {
    try {
      await apiPost('/api/advance-payments', paymentData);
      toast.success('Payment added successfully');
      fetchSummary();
      
      // Refresh details modal if open
      if (detailsModal.isOpen) {
        handleViewDetails(detailsModal.supplier);
      }
    } catch (error) {
      toast.error('Failed to add payment');
      throw error;
    }
  };
  
  // Handle settle payment
  const handleSettlePayment = async (paymentId, settleData) => {
    try {
      await apiPost(`/api/advance-payments/${paymentId}/settle`, settleData);
      toast.success('Payment settled successfully');
      fetchSummary();
      
      // Refresh details modal if open
      if (detailsModal.isOpen) {
        handleViewDetails(detailsModal.supplier);
      }
    } catch (error) {
      toast.error('Failed to settle payment');
      throw error;
    }
  };
  
  // Handle delete payment
  const handleDeletePayment = async (payment) => {
    if (!window.confirm('Are you sure you want to delete this payment record?')) return;
    
    try {
      await apiDelete(`/api/advance-payments/${payment.id}`);
      toast.success('Payment deleted successfully');
      fetchSummary();
      
      // Refresh details modal if open
      if (detailsModal.isOpen) {
        handleViewDetails(detailsModal.supplier);
      }
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };
  
  // Generate PDF Report for supplier
  const handleExportReport = async (supplier) => {
    try {
      toast.loading('Generating report...', { id: 'export' });
      
      // Fetch supplier payments
      const data = await apiGet(`/api/advance-payments/supplier/${supplier.supplierId}`);
      const payments = data.payments || [];
      
      // Create PDF with error handling
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header
      doc.setFillColor(15, 23, 42); // slate-900
      doc.rect(0, 0, pageWidth, 50, 'F');
      
      // Company Logo/Title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('ACCOUNT STATEMENT', pageWidth / 2, 25, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Supplier Advance Payments Report', pageWidth / 2, 35, { align: 'center' });
      
      // Date
      doc.setFontSize(8);
      doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')} at ${new Date().toLocaleTimeString()}`, pageWidth / 2, 45, { align: 'center' });
      
      // Supplier Info Section
      doc.setTextColor(0, 0, 0);
      doc.setFillColor(241, 245, 249); // slate-100
      doc.rect(10, 55, pageWidth - 20, 30, 'F');
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Supplier Information', 15, 65);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Name: ${supplier.supplierName || 'N/A'}`, 15, 73);
      doc.text(`Country: ${supplier.supplierCountry || 'Not specified'}`, 15, 80);
      
      // Summary Box
      doc.setFillColor(15, 23, 42);
      doc.rect(pageWidth - 80, 57, 65, 25, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.text('OUTSTANDING BALANCE', pageWidth - 47.5, 64, { align: 'center' });
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      // Fix: Use RGB values instead of hex string
      if (supplier.outstanding > 0) {
        doc.setTextColor(249, 115, 22); // orange-500
      } else {
        doc.setTextColor(16, 185, 129); // emerald-500
      }
      doc.text(`AED ${(supplier.outstanding || 0).toLocaleString()}`, pageWidth - 47.5, 76, { align: 'center' });
      
      // Financial Summary
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Financial Summary', 15, 100);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      // Summary table - with null safety
      const totalPaid = supplier.totalPaid || 0;
      const totalSettled = supplier.totalSettled || 0;
      const outstanding = supplier.outstanding || 0;
      const supplierName = supplier.supplierName || 'Unknown';
      
      const summaryData = [
        ['Total Advances Paid', `AED ${totalPaid.toLocaleString()}`],
        ['Total Settled/Recovered', `AED ${totalSettled.toLocaleString()}`],
        ['Outstanding Balance', `AED ${outstanding.toLocaleString()}`],
        ['Number of Transactions', `${payments.length}`]
      ];
      
      // Use autoTable(doc, options) format for jspdf-autotable v5.x
      autoTable(doc, {
        startY: 105,
        head: [],
        body: summaryData,
        theme: 'plain',
        styles: { fontSize: 10, cellPadding: 3 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 80 },
          1: { halign: 'right' }
        },
        margin: { left: 15, right: 15 }
      });
      
      // Payment Details Table
      if (payments.length > 0) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        // Get final Y position from the previous table
        const previousTableFinalY = (doc).lastAutoTable?.finalY || 130;
        doc.text('Transaction History', 15, previousTableFinalY + 15);
        
        const tableData = payments.map((p, index) => [
          index + 1,
          p.paymentDate || 'N/A',
          (p.description || '').substring(0, 30) + ((p.description || '').length > 30 ? '...' : ''),
          (p.reason || '').substring(0, 25) + ((p.reason || '').length > 25 ? '...' : ''),
          `AED ${(p.amount || 0).toLocaleString()}`,
          `AED ${(p.settledAmount || 0).toLocaleString()}`,
          p.isSettled ? 'Settled' : 'Outstanding'
        ]);
        
        autoTable(doc, {
          startY: previousTableFinalY + 20,
          head: [['#', 'Date', 'Description', 'Reason', 'Amount', 'Settled', 'Status']],
          body: tableData,
          theme: 'striped',
          headStyles: { 
            fillColor: [15, 23, 42],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 8
          },
          styles: { fontSize: 8, cellPadding: 2 },
          columnStyles: {
            0: { cellWidth: 10, halign: 'center' },
            1: { cellWidth: 22 },
            2: { cellWidth: 40 },
            3: { cellWidth: 35 },
            4: { cellWidth: 25, halign: 'right' },
            5: { cellWidth: 25, halign: 'right' },
            6: { cellWidth: 20, halign: 'center' }
          },
          margin: { left: 10, right: 10 }
        });
      }
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Page ${i} of ${pageCount} | TADABER Workforce Management System`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }
      
      // Save PDF - with safe filename
      const safeFileName = supplierName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
      doc.save(`${safeFileName}_Account_Statement_${new Date().toISOString().split('T')[0]}.pdf`);
      
      toast.success('Report exported successfully!', { id: 'export' });
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report', { id: 'export' });
    }
  };
  
  // Filter suppliers with memoization for performance
  const filteredSuppliers = useMemo(() => {
    return summary?.suppliers?.filter(s => 
      s.supplierName.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];
  }, [summary?.suppliers, searchQuery]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 text-blue-400 animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-7xl mx-auto space-y-6"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-white" />
              </div>
              Supplier Advance Payments
            </h1>
            <p className="text-slate-400 mt-1">Track and manage advance payments to suppliers</p>
          </div>
          <Button
            onClick={fetchSummary}
            variant="outline"
            className="bg-slate-800/50 border-slate-700 text-white rounded-xl"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </motion.div>
        
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <SummaryCard
            title="Total Advanced"
            value={`AED ${(summary?.grandTotal?.totalPaid || 0).toLocaleString()}`}
            subtitle={`${summary?.grandTotal?.supplierCount || 0} suppliers`}
            icon={TrendingUp}
            color="from-blue-500/10 via-slate-800/60 to-slate-900/80"
            trend="up"
          />
          <SummaryCard
            title="Total Settled"
            value={`AED ${(summary?.grandTotal?.totalSettled || 0).toLocaleString()}`}
            subtitle="Recovered amount"
            icon={CheckCircle}
            color="from-emerald-500/10 via-slate-800/60 to-slate-900/80"
            trend="up"
          />
          <SummaryCard
            title="Outstanding"
            value={`AED ${(summary?.grandTotal?.outstanding || 0).toLocaleString()}`}
            subtitle="Pending settlement"
            icon={Clock}
            color="from-orange-500/10 via-slate-800/60 to-slate-900/80"
            trend={summary?.grandTotal?.outstanding > 0 ? 'down' : 'up'}
          />
          <SummaryCard
            title="Suppliers"
            value={summary?.grandTotal?.supplierCount || 0}
            subtitle="With advance payments"
            icon={Building2}
            color="from-purple-500/10 via-slate-800/60 to-slate-900/80"
          />
        </div>
        
        {/* Search */}
        <motion.div variants={itemVariants} className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search suppliers..."
            className="bg-slate-900/60 border-slate-700 text-white pl-12 py-6 rounded-2xl text-lg"
          />
        </motion.div>
        
        {/* Supplier Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSuppliers.length === 0 ? (
            <motion.div variants={itemVariants} className="col-span-full text-center py-16">
              <Building2 className="h-20 w-20 mx-auto mb-4 text-slate-600" />
              <p className="text-slate-500 text-lg">No suppliers found</p>
              <p className="text-slate-600 text-sm mt-1">Add suppliers in the Suppliers section to track advance payments</p>
            </motion.div>
          ) : (
            filteredSuppliers.map(supplier => (
              <SupplierPaymentCard
                key={supplier.supplierId}
                supplier={supplier}
                onViewDetails={handleViewDetails}
                onAddPayment={(s) => setAddPaymentModal({ isOpen: true, supplier: s })}
                onExportReport={handleExportReport}
              />
            ))
          )}
        </div>
      </motion.div>
      
      {/* Modals */}
      <AddPaymentModal
        isOpen={addPaymentModal.isOpen}
        onClose={() => setAddPaymentModal({ isOpen: false, supplier: null })}
        supplier={addPaymentModal.supplier}
        onSubmit={handleAddPayment}
      />
      
      <SettlePaymentModal
        isOpen={settleModal.isOpen}
        onClose={() => setSettleModal({ isOpen: false, payment: null })}
        payment={settleModal.payment}
        onSubmit={handleSettlePayment}
      />
      
      <SupplierDetailsModal
        isOpen={detailsModal.isOpen}
        onClose={() => setDetailsModal({ isOpen: false, supplier: null, payments: [] })}
        supplier={detailsModal.supplier}
        payments={detailsModal.payments}
        onSettle={(payment) => {
          setDetailsModal(prev => ({ ...prev, isOpen: false }));
          setSettleModal({ isOpen: true, payment });
        }}
        onDelete={handleDeletePayment}
        onExportReport={handleExportReport}
      />
    </div>
  );
};

export default AdvancePaymentsPage;
